import React from 'react';
import {browserHistory} from 'react-router';
import moment from 'moment';
import CallActions from 'actions/CallActions';
import SearchInputActions from 'actions/search/SearchInputActions';
import ManagerDashboardActions from 'actions/ManagerDashboardActions';
import alert from 'utils/alert';
import AuthStore from 'stores/AuthStore';
import UserStore from 'stores/UserStore';
import buildUrl from 'components/dashboard/buildUrl';
import UserSource from 'sources/UserSource';
import {getCoachRepLink} from 'components/dashboard/coach/CoachSummaryDashboard';
import {LIBRARY_ROUTE} from '@store/library/library.consts';
import {scrollToTop} from 'utils/scrollToTop';

/**
 * Base widget class to consolidate common functionality and data
 */
export default class WidgetBase extends React.Component {
  /**
   * A convenience method for the other widgets so they don't have to worry about the managerId
   * @param widgetConfig  specific Config object for the widget requesting the data
   */
  getWidgetData(widgetConfig) {
    ManagerDashboardActions.getWidget.defer(widgetConfig, this.props.managerId);
  }

  setWidgetState(widgetId, value) {
    ManagerDashboardActions.setWidgetState(widgetId, value);
  }

  getWidgetState(widgetId) {
    return ManagerDashboardActions.getWidgetState(widgetId);
  }

  getDashboardForManager(managerId, adminId, page, state = null) {
    const path = adminId
      ? buildUrl({
          managerId: managerId,
          adminId: adminId,
        }) + (page ? `/${page}` : '')
      : '/stats';
    const params = adminId ? {} : {managerId};
    const link = {
      pathname: path,
      query: params,
    };
    if (state) {
      link.state = state;
    }
    browserHistory.push(link);
    scrollToTop();
  }

  alertManagerInactive(team) {
    alert(
      `The manager for ${team.name} is no longer active in Coach, and their team dashboard is no longer available.
      To see performance of agents on this team, please assign a new manager to this team.`
    );
  }

  alertUserInactive(user) {
    alert(`${user.displayName} is no longer active in Coach`);
  }

  getManagerId() {
    const authUser = AuthStore.getState().user;
    return this.props.managerId || authUser.id;
  }

  showCalls(calls, totalCalls, limitUsed) {
    this.deferClearSearch();
    this.setShouldResetSearch(false);

    const limitWarning =
      totalCalls >= limitUsed &&
      'Only the most recent calls matching this Smart Alert are shown for performance reasons.';

    const backUrl = location.pathname + location.search;
    const backText = 'Back to Dashboard';

    CallActions.setDrilldown([backUrl, backText, limitWarning, calls]);

    browserHistory.push({
      pathname: 'calls/my',
      state: {
        callIds: calls,
      },
    });

    scrollToTop();
  }

  showLibrary(libraryId) {
    browserHistory.push(LIBRARY_ROUTE.SEARCH_LIBRARY(libraryId));
    scrollToTop();
  }

  async showRepMetrics(periodInDays, userId, teamId = null, state = null) {
    let rep = UserStore.getState().users.get(userId);
    if (!rep) {
      const users = await UserSource.fetchAll({ids: [userId]});
      if (users && !users.isEmpty()) {
        rep = users.get(0);
      }
    }
    const link = {};
    if (state) {
      link.state = state;
    }
    const endDateDefault = moment();
    const startDateDefault = endDateDefault.clone().subtract(periodInDays - 1, 'days');
    const {
      startDate: propsStartDate,
      endDate: propsEndDate,
      adminId,
      managerId,
      location,
      fromPath,
    } = this.props;
    const startDate = propsStartDate ? propsStartDate : startDateDefault;
    const endDate = propsEndDate ? propsEndDate : endDateDefault;
    const props = {adminId, managerId, location, fromPath, startDate, endDate};
    const linkWithAddedInfo = getCoachRepLink({rep, repId: userId, teamId}, props, link, false);
    browserHistory.push(linkWithAddedInfo);
    scrollToTop();
  }

  /**
   * Helper function to set the switch for resetting the search
   * @param shouldResetSearch  true/false
   * @param defer  sometimes this method is called during dispatch.  If so, defer = true should be passed
   */
  setShouldResetSearch(shouldResetSearch, defer = false) {
    defer
      ? ManagerDashboardActions.setShouldResetSearch.defer(shouldResetSearch)
      : ManagerDashboardActions.setShouldResetSearch(shouldResetSearch);
  }

  deferClearSearch() {
    SearchInputActions.clearSearchInputValue.defer();
    CallActions.clearSearch.defer();
  }
}
